import {
    createContext,
    useContext,
    useReducer,
    useEffect,
    useCallback,
    type PropsWithChildren,
} from "react";
import * as authService from "@/api/authService";
import Spinner from "@/components/common/spinner/Spinner";
import { authReducer, initialState, type authState } from "./authReducer";
import type { user } from "@/types/api/user.api";
import type { authStatusResponse } from "@/features/login/types.api";

export interface AuthContextType extends authState {
    login: (user: user, token: string) => void;
    logout: () => Promise<void>;
    updateUser: (patch: Partial<user>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    useEffect(() => {
        const verifyUserSession = async () => {
            dispatch({ type: "SET_LOADING", payload: true });
            const storedToken = localStorage.getItem("accessToken");

            if (storedToken) {
                try {
                    const authStatus: authStatusResponse = await authService.authStatus();

                    if (authStatus.user) {
                        localStorage.setItem("user", JSON.stringify(authStatus.user));
                        dispatch({
                            type: "LOGIN",
                            payload: { user: authStatus.user, accessToken: storedToken },
                        });
                    } else {
                        localStorage.removeItem("accessToken");
                        localStorage.removeItem("user");
                        dispatch({ type: "LOGOUT" });
                    }
                } catch (error) {
                    console.warn(
                        "Error al verificar la sesión con el backend (puede ser token inválido/expirado):",
                        error,
                    );
                    localStorage.removeItem("accessToken");
                    localStorage.removeItem("user");
                    dispatch({ type: "LOGOUT" });
                }
            } else {
                dispatch({ type: "SET_LOADING", payload: false });
            }
        };

        verifyUserSession();
    }, []);

    const login = useCallback((userData: user, accessToken: string) => {
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("user", JSON.stringify(userData));
        dispatch({ type: "LOGIN", payload: { user: userData, accessToken } });
    }, []);

    const logout = useCallback(async () => {
        dispatch({ type: "SET_LOADING", payload: true });
        try {
            await authService.logout();
        } catch (error) {
            console.error("Error durante el cierre de sesión en el backend:", error);
        } finally {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("user");
            dispatch({ type: "LOGOUT" });
        }
    }, []);

    const updateUser = useCallback((patch: Partial<user>) => {
        dispatch({ type: "UPDATE_USER", payload: patch });
    }, []);

    return (
        <AuthContext.Provider value={{ ...state, login, logout, updateUser }}>
            {!state.isLoading ? children : <Spinner text="Cargando sesión..." />}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth debe ser usado dentro de un AuthProvider");
    }
    return context;
};
